/**
 * Safe hydration strategy for Vue async components that handles dynamic keys
 *
 * Unlike Vue's built-in hydrateOnVisible, this version checks if DOM elements
 * are still connected before attempting hydration, preventing errors when
 * elements are removed due to key changes during rapid navigation.
 */

function elementIsVisibleInViewport(el: Element): boolean {
  const { top, left, bottom, right } = el.getBoundingClientRect()
  const { innerHeight, innerWidth } = window
  return (
    ((top > 0 && top < innerHeight) || (bottom > 0 && bottom < innerHeight)) &&
    ((left > 0 && left < innerWidth) || (right > 0 && right < innerWidth))
  )
}

function getComponentIdentifier(el: Element): string {
  // Try to get component name from data attributes or class names
  const key = el.getAttribute('data-v-inspector') || el.getAttribute('data-key')
  const className = el.className
  const tagName = el.tagName.toLowerCase()

  if (key) return `${tagName}[key="${key}"]`
  if (className) return `${tagName}.${className.split(' ')[0]}`
  return tagName
}

export const hydrateOnVisibleSafe =
  (opts?: IntersectionObserverInit) =>
  (
    hydrate: () => void,
    forEach: (callback: (el: Element) => void | false) => void,
  ): (() => void) => {
    const ob = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue

        const componentId = getComponentIdentifier(e.target as Element)

        // Check if element is still connected to DOM before hydrating
        if (!e.target.isConnected) {
          console.warn(
            `[hydrateOnVisibleSafe] Element disconnected during intersection, stopping observation: ${componentId}`,
          )
          ob.disconnect()
          return
        }

        try {
          ob.disconnect()
          hydrate()
          console.debug(
            `[hydrateOnVisibleSafe] Successfully hydrated: ${componentId}`,
          )
          break
        } catch (error) {
          console.error(
            `[hydrateOnVisibleSafe] Hydration failed for ${componentId}:`,
            error,
          )
          // Continue processing other elements instead of crashing
          break
        }
      }
    }, opts)

    forEach((el) => {
      if (!(el instanceof Element)) return

      const componentId = getComponentIdentifier(el)

      // Check if element is connected before setting up observer
      if (!el.isConnected) {
        console.warn(
          `[hydrateOnVisibleSafe] Element not connected during setup: ${componentId}`,
        )
        return false
      }

      if (elementIsVisibleInViewport(el)) {
        try {
          hydrate()
          ob.disconnect()
          console.debug(
            `[hydrateOnVisibleSafe] Immediately hydrated visible element: ${componentId}`,
          )
          return false
        } catch (error) {
          console.error(
            `[hydrateOnVisibleSafe] Immediate hydration failed for ${componentId}:`,
            error,
          )
          // Skip this element but continue with others
          return false
        }
      }

      ob.observe(el)
      console.debug(`[hydrateOnVisibleSafe] Observing element: ${componentId}`)
      // Continue processing more elements (explicit return for TypeScript)
      return
    })

    return () => ob.disconnect()
  }
